import { LanguageKeys } from 'src/common/constants/languages';
import { collectUrlsFromHtml } from 'src/helpers/uploadFileUrls';
import { forEachPageBlock, IPageDocument } from 'src/helpers/pageDocumentUtils';
import { ILogoBlock, ITextBlock } from 'src/interfaces/PageBlock';
import { ILegacyPageContentSection } from 'src/interfaces/PageContent';

interface PageContentFileUsageRef {
  fileId: string;
  fieldName: string;
}

interface PageContentTextFileUsageRef {
  fileUrl: string;
  fieldName: string;
}

interface ResolvedPageContentTextFileUsageRef {
  fileId: string;
  fieldName: string;
}

const CONTENT_LANGUAGES: LanguageKeys[] = [
  LanguageKeys.en,
  LanguageKeys.hu,
  LanguageKeys.ro,
];

export function collectDocumentHeroFileUsageRefs(
  doc: IPageDocument | undefined,
): PageContentFileUsageRef[] {
  if (!doc) return [];

  const refs: PageContentFileUsageRef[] = [];
  const backgroundId = doc.heroSettings?.backgroundImageFileId?.trim();
  if (backgroundId) {
    refs.push({ fileId: backgroundId, fieldName: 'hero-background' });
  }

  forEachPageBlock(doc, (block, ctx) => {
    if (ctx.zone !== 'hero' || block.type !== 'logo') return;
    const logo = block as ILogoBlock;
    const logoFileId = logo.imageFileId?.trim();
    if (logoFileId) {
      refs.push({ fileId: logoFileId, fieldName: `block-${block.id}` });
    }
  });

  return refs;
}

export function collectDocumentTextFileUsageRefs(
  doc: IPageDocument | undefined,
  legacySections?: ILegacyPageContentSection[],
): PageContentTextFileUsageRef[] {
  const refs: PageContentTextFileUsageRef[] = [];
  const seen = new Set<string>();

  const addUrl = (fileUrl: string, fieldName: string) => {
    const key = `${fileUrl}:${fieldName}`;
    if (seen.has(key)) return;
    seen.add(key);
    refs.push({ fileUrl, fieldName });
  };

  if (doc) {
    forEachPageBlock(doc, block => {
      if (block.type !== 'text') return;
      const textBlock = block as ITextBlock;
      for (const lang of CONTENT_LANGUAGES) {
        for (const fileUrl of collectUrlsFromHtml(textBlock.i18n[lang])) {
          addUrl(fileUrl, `block-${block.id}`);
        }
      }
    });
  }

  if (legacySections) {
    for (const section of legacySections) {
      for (const lang of CONTENT_LANGUAGES) {
        for (const fileUrl of collectUrlsFromHtml(section.i18n?.[lang])) {
          addUrl(fileUrl, `legacy-${section.name}`);
        }
      }
    }
  }

  return refs;
}

export type MarkFileUsageFn = (
  fileId: string,
  isUsed: boolean,
  contentType: string,
  contentId: string,
  fieldName: string,
  options?: { silent?: boolean },
) => Promise<void>;

type ResolveFileIdsByUrlsFn = (urls: string[]) => Promise<Map<string, string>>;

async function resolveTextFileUsageRefs(
  refs: PageContentTextFileUsageRef[],
  resolveFileIdsByUrls: ResolveFileIdsByUrlsFn,
): Promise<ResolvedPageContentTextFileUsageRef[]> {
  if (refs.length === 0) return [];

  const uniqueUrls = [...new Set(refs.map(ref => ref.fileUrl))];
  const urlToFileId = await resolveFileIdsByUrls(uniqueUrls);
  const resolved: ResolvedPageContentTextFileUsageRef[] = [];

  for (const ref of refs) {
    const fileId = urlToFileId.get(ref.fileUrl);
    if (fileId) {
      resolved.push({ fileId, fieldName: ref.fieldName });
    }
  }

  return resolved;
}

export async function syncHeroSectionFileUsage(
  markFileUsage: MarkFileUsageFn,
  contentId: string,
  previousDoc: IPageDocument | undefined,
  nextDoc: IPageDocument | undefined,
  options?: {
    additionalContentIdsForUnmark?: string[];
    markAddedRefs?: boolean;
  },
): Promise<void> {
  const contentType = 'pageContent';
  const previousRefs = collectDocumentHeroFileUsageRefs(previousDoc);
  const nextRefs = collectDocumentHeroFileUsageRefs(nextDoc);
  const unmarkContentIds = Array.from(
    new Set([contentId, ...(options?.additionalContentIdsForUnmark ?? [])]),
  );
  const shouldMarkAddedRefs = options?.markAddedRefs ?? true;

  const previousKeys = new Set(previousRefs.map(ref => `${ref.fileId}:${ref.fieldName}`));
  const nextKeys = new Set(nextRefs.map(ref => `${ref.fileId}:${ref.fieldName}`));

  if (shouldMarkAddedRefs) {
    for (const ref of nextRefs) {
      const key = `${ref.fileId}:${ref.fieldName}`;
      if (!previousKeys.has(key)) {
        await markFileUsage(ref.fileId, true, contentType, contentId, ref.fieldName, {
          silent: true,
        });
      }
    }
  }

  for (const ref of previousRefs) {
    const key = `${ref.fileId}:${ref.fieldName}`;
    if (!nextKeys.has(key)) {
      for (const unmarkContentId of unmarkContentIds) {
        await markFileUsage(ref.fileId, false, contentType, unmarkContentId, ref.fieldName, {
          silent: true,
        });
      }
    }
  }
}

export async function syncPageContentTextFileUsage(
  markFileUsage: MarkFileUsageFn,
  resolveFileIdsByUrls: ResolveFileIdsByUrlsFn,
  contentId: string,
  previousDoc: IPageDocument | undefined,
  nextDoc: IPageDocument | undefined,
  options?: {
    previousLegacySections?: ILegacyPageContentSection[];
    nextLegacySections?: ILegacyPageContentSection[];
    additionalContentIdsForUnmark?: string[];
  },
): Promise<void> {
  const contentType = 'pageContent';
  const unmarkContentIds = Array.from(
    new Set([contentId, ...(options?.additionalContentIdsForUnmark ?? [])]),
  );

  const previousRefs = await resolveTextFileUsageRefs(
    collectDocumentTextFileUsageRefs(previousDoc, options?.previousLegacySections),
    resolveFileIdsByUrls,
  );
  const nextRefs = await resolveTextFileUsageRefs(
    collectDocumentTextFileUsageRefs(nextDoc, options?.nextLegacySections),
    resolveFileIdsByUrls,
  );

  const nextKeys = new Set(nextRefs.map(ref => `${ref.fileId}:${ref.fieldName}`));

  for (const ref of nextRefs) {
    await markFileUsage(ref.fileId, true, contentType, contentId, ref.fieldName, { silent: true });
  }

  for (const ref of previousRefs) {
    const key = `${ref.fileId}:${ref.fieldName}`;
    if (!nextKeys.has(key)) {
      for (const unmarkContentId of unmarkContentIds) {
        await markFileUsage(ref.fileId, false, contentType, unmarkContentId, ref.fieldName, {
          silent: true,
        });
      }
    }
  }
}
